import { createRouter, createWebHistory } from 'vue-router';
import axios from 'axios';
import routes from './routes';
import { clearSession, tokenExpirado } from '@/utils/session';
import { hasFeature, setModulos, setPlanCodigo } from '@/utils/features';
import { isOnline, isOfflineAllowedPath, refreshConnectivity } from '@/utils/connectivity';

const router = createRouter({
    history: createWebHistory(),
    routes,
    linkActiveClass: 'active',
    linkExactActiveClass: 'active',
});

function normalizeAuthPath(path) {
    const decoded = decodeURIComponent(String(path || ''));
    if (decoded.startsWith('/u/restablecer-contrase')) {
        return '/u/restablecer-contrasena';
    }
    if (decoded.startsWith('/u/recuperar-contrase')) {
        return '/u/recuperar-contrasena';
    }
    return null;
}

router.beforeEach(async (to, from, next) => {
    void refreshConnectivity();
    const authPath = normalizeAuthPath(to.path);
    if (authPath && authPath !== to.path) {
        next({ path: authPath, query: to.query, hash: to.hash, replace: true });
        return;
    }

    const token = localStorage.getItem('token');
    const usuario = localStorage.getItem('usuario');
    const rol = localStorage.getItem('rol') || (usuario === 'admin' ? 'admin' : 'negocio');

    if (to.matched.some((record) => record.meta && record.meta.requiresAuth)) {
        if (!token) {
            next('/');
            return;
        }
        if (tokenExpirado(token)) {
            // Offline: mantener sesión local para POS/cobros con cache en IndexedDB.
            if (!isOnline() && isOfflineAllowedPath(to.path)) {
                /* continuar */
            } else {
                clearSession();
                next('/u/login');
                return;
            }
        }
        if (!isOnline() && !isOfflineAllowedPath(to.path)) {
            if (hasFeature('pos')) {
                next('/u/pos');
                return;
            }
            if (to.path !== '/u/home') {
                next('/u/home');
                return;
            }
        }
        if (usuario && rol !== 'admin') {
            const exempt = to.matched.some((record) => record.meta && record.meta.allowWhenPlanSuspended);
            if (!exempt && isOnline()) {
                try {
                    const { data } = await axios.get('/miNegocio', { params: { usuario } });
                    if (data && Array.isArray(data.modulos)) {
                        setModulos(data.modulos);
                        setPlanCodigo(data.planCodigo || 'legacy_full');
                    }
                    if (data && data.planSuspendido) {
                        if (rol === 'vendedor') {
                            clearSession();
                            next({ path: '/u/login', query: { planVendedorSuspendido: '1' } });
                            return;
                        }
                        next({ path: '/u/planes', query: { vencido: '1', avisoPlanSuspendido: '1' } });
                        return;
                    }
                } catch (error) {
                    console.error('No se pudo verificar el estado actual del plan:', error);
                }
            }
        }
        // Restricción por rol: meta.roles = ['admin','negocio','vendedor']
        const reglas = to.matched
            .map((record) => record.meta && record.meta.roles)
            .filter(Boolean);
        if (reglas.length && !reglas.every((roles) => roles.includes(rol))) {
            next('/u/home');
            return;
        }
        const featRules = to.matched
            .map((record) => record.meta && record.meta.features)
            .filter(Boolean);
        const offlineAllowed = !isOnline() && isOfflineAllowedPath(to.path);
        if (featRules.length && rol !== 'admin' && !offlineAllowed) {
            const required = featRules[featRules.length - 1];
            if (!required.every((f) => hasFeature(f))) {
                next('/u/modulo-no-incluido');
                return;
            }
        }
        const featAnyRules = to.matched
            .map((record) => record.meta && record.meta.featuresAny)
            .filter(Boolean);
        if (featAnyRules.length && rol !== 'admin' && !offlineAllowed) {
            const anyReq = featAnyRules[featAnyRules.length - 1];
            const ok = anyReq.some((f) => hasFeature(f));
            if (!ok) {
                next('/u/modulo-no-incluido');
                return;
            }
        }
        next();
        return;
    }

    next();
});

export default router;
